/**
 * Super Admin Login Page
 *
 * Secure login for platform administrators.
 */
import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Shield } from 'lucide-react';
import { Button, Input, Card, CardContent } from '@respectly/ui';
import { useAuth } from '@respectly/utils/auth';
import { AuthLayout } from './components/AuthLayout';

export function Login() {
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setIsLoading(true);
    const result = await login(email, password);
    setIsLoading(false);

    if (!result.success) {
      setError(result.error || 'Invalid credentials');
    } else {
      // Redirect to dashboard after successful login
      window.location.href = '/dashboard';
    }
  };

  return (
    <AuthLayout
      title="Administrator Sign In"
      subtitle="Access the platform administration portal"
    >
      <Card className="border-0 bg-slate-800 shadow-2xl">
        <CardContent className="p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Security notice */}
            <div className="flex items-center gap-2 rounded-lg bg-slate-700/50 p-3 text-sm text-slate-300">
              <Shield className="h-4 w-4 shrink-0 text-primary-400" />
              <span>This portal is for authorized administrators only.</span>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-900/50 p-3 text-sm text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email field */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-slate-300">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-700 border-slate-600 pl-10 text-white placeholder:text-slate-500 focus:border-primary-500"
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-300">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-700 border-slate-600 pl-10 pr-10 text-white placeholder:text-slate-500 focus:border-primary-500"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              className="w-full bg-primary-600 hover:bg-primary-700"
              size="lg"
              isLoading={isLoading}
            >
              Sign In to Admin Portal
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export default Login;
